/**
 * CommunityJournals - Grid of shared journals for Explore page
 * 
 * Design: Apple template gallery inspired
 * - Featured hero journal at top
 * - Simplified cards (cover + title + creator)
 * - Hover: scale + shadow
 * - No view/copy counts on cards (cleaner)
 */

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCommunityJournals, useCommunityJournalTags } from "@/hooks/useCommunityJournals";
import { Skeleton } from "@/components/ui/skeleton";
import { User } from "lucide-react";

export const CommunityJournals = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTag = searchParams.get('tag');
    const [selectedCategory, setSelectedCategory] = useState(initialTag || 'recommend');
    const navigate = useNavigate();
    const { data: journals, isLoading } = useCommunityJournals(selectedCategory);
    const { data: uniqueTags, isLoading: isLoadingTags } = useCommunityJournalTags();

    // Update URL when category changes
    const handleCategorySelect = (categoryId: string) => {
        setSelectedCategory(categoryId);
        if (categoryId === 'recommend' || categoryId === 'featured') {
            searchParams.delete('tag');
        } else {
            searchParams.set('tag', categoryId);
        }
        setSearchParams(searchParams, { replace: true });
    };

    // Build dynamic categories: Recommended, Featured, then all unique tags
    const categories = [
        { id: 'recommend', label: 'Recommended' },
        { id: 'featured', label: 'Featured' },
        ...(uniqueTags || []).map(tag => ({ id: tag, label: tag })),
    ];

    const filteredJournals = journals || [];

    // Get featured journal (first one = highest views since data is sorted by view_count DESC)
    const featuredJournal = filteredJournals[0];
    const otherJournals = filteredJournals.filter(j => j.id !== featuredJournal?.id);

    const handleJournalClick = (journalId: string) => {
        navigate(`/community/journal/${journalId}`);
    };

    return (
        <div className="space-y-6 w-full max-w-5xl mx-auto">
            {/* Category Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {categories.map(category => (
                    <button
                        key={category.id}
                        onClick={() => handleCategorySelect(category.id)}
                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${selectedCategory === category.id
                            ? 'bg-gray-900 text-white'
                            : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                            }`}
                    >
                        {category.label}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <div className="space-y-6">
                    {/* Hero skeleton */}
                    <Skeleton className="h-64 w-full rounded-2xl" />
                    {/* Grid skeleton */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="bg-white rounded-2xl p-4">
                                <Skeleton className="h-36 w-full rounded-xl mb-3" />
                                <Skeleton className="h-5 w-3/4 mb-2" />
                                <Skeleton className="h-4 w-1/2" />
                            </div>
                        ))}
                    </div>
                </div>
            ) : filteredJournals.length > 0 ? (
                <>
                    {/* Featured Hero Journal */}
                    {featuredJournal && (
                        <div
                            onClick={() => handleJournalClick(featuredJournal.id)}
                            className="relative group cursor-pointer rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300"
                        >
                            {/* Cover Image */}
                            {featuredJournal.community_cover_url ? (
                                <img
                                    src={featuredJournal.community_cover_url}
                                    alt={featuredJournal.name}
                                    className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                />
                            ) : (
                                <div className="w-full h-64 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                                    <span className="text-6xl opacity-40">📓</span>
                                </div>
                            )}

                            {/* Overlay Content */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                <h2 className="text-2xl font-semibold mb-2">{featuredJournal.name}</h2>
                                <div className="flex items-center gap-2 text-white/80">
                                    {featuredJournal.profiles?.avatar_url ? (
                                        <img
                                            src={featuredJournal.profiles.avatar_url}
                                            alt=""
                                            className="w-5 h-5 rounded-full"
                                        />
                                    ) : (
                                        <User className="w-4 h-4" />
                                    )}
                                    <span className="text-sm">{featuredJournal.profiles?.full_name || 'Anonymous'}</span>
                                </div>
                            </div>

                            {/* Featured badge */}
                            <div className="absolute top-4 left-4">
                                <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-medium rounded-full">
                                    Featured
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Journals Grid */}
                    {otherJournals.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {otherJournals.map(journal => (
                                <div
                                    key={journal.id}
                                    onClick={() => handleJournalClick(journal.id)}
                                    className="bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-md group border border-gray-100"
                                >
                                    {/* Cover Image */}
                                    {journal.community_cover_url ? (
                                        <img
                                            src={journal.community_cover_url}
                                            alt={journal.name}
                                            className="w-full h-36 object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                        />
                                    ) : (
                                        <div className="w-full h-36 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                                            <span className="text-4xl opacity-40">📓</span>
                                        </div>
                                    )}

                                    {/* Content */}
                                    <div className="p-4">
                                        <h3 className="font-medium text-gray-900 line-clamp-1 mb-2">
                                            {journal.name}
                                        </h3>

                                        {/* Creator */}
                                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                                            {journal.profiles?.avatar_url ? (
                                                <img
                                                    src={journal.profiles.avatar_url}
                                                    alt=""
                                                    className="w-4 h-4 rounded-full"
                                                />
                                            ) : (
                                                <User className="w-3.5 h-3.5" />
                                            )}
                                            <span className="truncate">
                                                {journal.profiles?.full_name || 'Anonymous'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-16 text-gray-500">
                    <span className="text-5xl mb-4 block">📓</span>
                    <p>No journals found in this category.</p>
                </div>
            )}
        </div>
    );
};

export default CommunityJournals;
